/**!
 * Created by Leonardo Cheslacki on 10/12/2024.
 *
 * MIT License
 *
 * Copyright (c) 2024 cheslacki
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.

 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import PropTypes from 'prop-types';

import SupportController from './SupportController';

import Big from 'big.js';

import VARIABLES from './../../../../variables.json';

import WithType from './../helpers/WithType';

let _this = {};

/**
 *
 */
class SupportTouchCanvas extends SupportController {

    /**
     *
     * @param props
     * @param main
     */
    constructor(props, main = {}) {
        super(props);
        this.props = props;
        this.main = main;

        this.state = {};

        _this = this;
    };

    $functions = {

        /**
         *
         * @param event
         * @param data
         * @private
         */
        _handleMouseDown: function (event, data) {
            const {occ, occ: {stage}} = _this.props;

            let
                big_mouseX = (new Big(stage.mouseX)),
                big_mouseY = (new Big(stage.mouseY)),
                big_scaleX = (new Big(stage.scaleX)),
                big_scaleY = (new Big(stage.scaleY)),
                big_occX = (new Big(occ.x)),
                big_occY = (new Big(occ.y)),
                big_clientX = big_mouseX.div(big_scaleX),
                big_clientY = big_mouseY.div(big_scaleY);

            let state = {
                big_offsetX: big_clientX.minus(big_occX),
                big_offsetY: big_clientY.minus(big_occY)
            };

            _this.setStateOccurrence(occ, state);

            (typeof (_this.props.callback) === 'function') && _this.props.callback(event, {
                ...data,
                state: {
                    ...state,
                    big_clientX: big_clientX,
                    big_clientY: big_clientY
                }
            });
        },

        /**
         *
         * @param event
         * @param data
         * @private
         */
        _handleMouseMove: function (event, data) {
            const
                {occ, occ: {stage}} = _this.props,
                {big_offsetX, big_offsetY} = occ.state;

            let
                big_mouseX = (new Big(stage.mouseX)),
                big_mouseY = (new Big(stage.mouseY)),
                big_scaleX = (new Big(stage.scaleX)),
                big_scaleY = (new Big(stage.scaleY)),
                big_clientX = big_mouseX.div(big_scaleX),
                big_clientY = big_mouseY.div(big_scaleY);

            let state = {
                big_positionX: big_clientX.minus(big_offsetX),
                big_positionY: big_clientY.minus(big_offsetY)
            };

            (typeof (_this.props.callback) === 'function') && _this.props.callback(event, {
                ...data,
                state: {
                    ...state,
                    big_clientX: big_clientX,
                    big_clientY: big_clientY
                }
            });
        },

        /**
         *
         * @param event
         * @param data
         * @private
         */
        _handleTouchStart: function (event, data) {
            const {occ, occ: {stage}, properties: {width, height}} = _this.props;

            let
                target_touch = event.targetTouches.item(0),
                big_clientX = _this.calcSize(stage.canvas.clientWidth, width, target_touch.clientX, true),
                big_clientY = _this.calcSize(stage.canvas.clientHeight, height, target_touch.clientY, true),
                big_occX = (new Big(occ.x)),
                big_occY = (new Big(occ.y));

            let state = {
                big_offsetX: big_clientX.minus(big_occX),
                big_offsetY: big_clientY.minus(big_occY)
            };

            _this.setStateOccurrence(occ, state);

            (typeof (_this.props.callback) === 'function') && _this.props.callback(event, {
                ...data,
                state: {
                    ...state,
                    big_clientX: big_clientX,
                    big_clientY: big_clientY
                }
            });
        },

        /**
         *
         * @param event
         * @param data
         * @private
         */
        _handleTouchMove: function (event, data) {
            _this.stopPropagation(event);

            const
                {occ, occ: {stage}, properties: {width, height}} = _this.props,
                {big_offsetX, big_offsetY} = occ.state;

            /**
             * Resized auto from scale native.
             * @type {any}
             */
            let
                target_touch = event.targetTouches.item(0),
                big_clientX = _this.calcSize(stage.canvas.clientWidth, width, target_touch.clientX, true),
                big_clientY = _this.calcSize(stage.canvas.clientHeight, height, target_touch.clientY, true);

            let state = {
                big_positionX: big_clientX.minus(big_offsetX),
                big_positionY: big_clientY.minus(big_offsetY)
            };

            (typeof (_this.props.callback) === 'function') && _this.props.callback(event, {
                ...data,
                state: {
                    ...state,
                    big_clientX: big_clientX,
                    big_clientY: big_clientY
                }
            });
        },

        /**
         *
         * @param event
         * @param data
         * @private
         */
        _handleTouchEnd: function (event, data) {
            (typeof (_this.props.callback) === 'function') && _this.props.callback(event, data);
        }
    };

    /**
     *
     * @param occ
     * @param big_clientX
     * @param big_clientY
     * @param x
     * @param y
     * @param width_diff
     * @param height_diff
     * @param test
     * @returns {*}
     */
    checkLimitation = (occ, big_clientX, big_clientY, x, y, width_diff = 0, height_diff = 0, test = null) => {
        let
            big_x = this.inObject(x, 'constructor') ? x : (new Big(x)),
            big_y = this.inObject(x, 'constructor') ? y : (new Big(y)),
            big_width_diff = this.inObject(width_diff, 'constructor') ? width_diff : (new Big(width_diff)),
            big_height_diff = this.inObject(height_diff, 'constructor') ? height_diff : (new Big(height_diff)),
            big_width = (new Big(occ.nominalBounds.width).plus(big_width_diff)),
            big_height = (new Big(occ.nominalBounds.height).plus(big_height_diff)),
            big_leftHorizontal = big_x,
            big_topVertical = big_y,
            big_rightHorizontal = big_leftHorizontal.plus(big_width),
            big_bottomVertical = big_topVertical.plus(big_height);

        test && this.testArea(occ.stage, big_width, big_height, big_x, big_y);

        /**
         * Limitation slider.
         */
        return ((big_clientX.gte(big_leftHorizontal) && big_clientX.lte(big_rightHorizontal)) && (big_clientY.gte(big_topVertical) && big_clientY.lte(big_bottomVertical)));
    };

    /**
     *
     * @param occ
     * @param type
     * @param event
     * @param options
     */
    addListenerHandle = (occ, type = '_MOUSE_DOWN_', event = 'animate', options = false | {}) => {
        let
            _TYPE_ = this.verifyField(VARIABLES.event, `${event}.${type}`),
            handle = this.toHandleName(type);

        if (event === 'native') {
            let reference = this.toSlug(handle);
            this.removeListener(_TYPE_, reference, () => {
                this.addListener(occ, _TYPE_, reference, this.$functions[handle], options, {type: type});
            });
        } else {
            this[handle] && occ.off(_TYPE_, this[handle]);
            this[handle] = occ.on(_TYPE_, this.$functions[handle], false, false, {type: type});
        }
    };

    startTouch = () => {
        const {occ, occ: {stage: {canvas}}} = this.props;
        this.addListenerHandle(occ);
        this.addListenerHandle(occ, '_MOUSE_MOVE_');
        this.addListenerHandle(canvas, '_TOUCH_START_', 'native', {passive: true});
        this.addListenerHandle(canvas, '_TOUCH_MOVE_', 'native');
        this.addListenerHandle(canvas, '_TOUCH_END_', 'native', {passive: true});
    };

    initTouch = () => {
        this.startTouch();
    };
}

/**
 *
 * @type {{}}
 */
SupportTouchCanvas.propTypes = {
    occ: PropTypes.object,
    properties: PropTypes.object
};

/**
 *
 * @type {{}}
 */
SupportTouchCanvas.defaultProps = {
    occ: null,
    properties: null
};

/**
 *
 * @param ClassObject
 * @param className {className: 'className'}
 * @returns {function(*): function({}=): *} pass _this ClassMain (required) and props {} (optional)
 */
export default WithType(SupportTouchCanvas, {className: 'SupportTouchCanvas'});
